import React from "react";
import cn from "classnames";
import { Link } from "react-router-dom";
import Styles from "./ListRow.module.scss";
import { Avatar, AvatarTypeOptions } from "../Avatar/Avatar";
import { Clickable } from "../Clickable/Clickable";
import * as Team from "src/state/modules/team";

export interface ListRowContainerProps {
  children: (typeof ListRow)[] | typeof ListRow | any | any[];
}

export const ListRowContainer = ({ children }: ListRowContainerProps) => {
  return <div className={Styles.listRowContainer}>{children}</div>;
};

export interface ListRowProps {
  mainContent: any;
  mainContentStyles?: string;
  secondaryContent?: any;
  // Rendered in the trailing "buttons" slot, OUTSIDE the row's navigation <Link> — use it for
  // interactive controls (e.g. external PG/PBR links) that must not be nested inside the row anchor.
  trailing?: any;
  onClick?: (x: string | undefined) => void;
  href?: string;
  dataID?: string;
  intitials?: string;

  avatar: { type: AvatarTypeOptions.Person } | { type: AvatarTypeOptions.Team; sport: Team.Sport; source?: string };
}

export const ListRow = (props: ListRowProps) => {
  let icon: JSX.Element;
  if (props.avatar.type === AvatarTypeOptions.Team) {
    icon = props.secondaryContent ? (
      <Avatar.Team sportIcon={props.avatar.sport} size={"medium"} />
    ) : (
      <Avatar.Team sportIcon={props.avatar.sport} source={props.avatar.source} size={"medium"} />
    );
  } else {
    icon = <Avatar.Person initials={props.intitials} />;
  }

  const Content = () => {
    let content;
    if (props.secondaryContent)
      content = (
        <div className={Styles.contentTwoRows}>
          <div className={Styles.mainContent}>{props.mainContent}</div>
          {props.secondaryContent && <div className={Styles.secondaryContent}>{props.secondaryContent}</div>}
        </div>
      );
    else content = <div className={cn(Styles.mainContent, props.mainContentStyles)}>{props.mainContent}</div>;
    return content;
  };

  const LinkListRow = () => (
    <div className={cn(Styles.container, Styles.listRow, Styles.oneRow)}>
      {/* display:contents on .rowLink so avatar + name still participate in the row grid,
          while the trailing slot sits beside them outside the anchor. */}
      <Link to={props.href || ""} className={Styles.rowLink}>
        <div className={cn(Styles.avatar)}>{icon}</div>
        <Content />
      </Link>
      {props.trailing ? <div className={Styles.buttons}>{props.trailing}</div> : null}
    </div>
  );

  // Shared row layout: the grid-template-areas live on .oneRow/.twoRow, so every branch must apply
  // one of them or the avatar/name/buttons collapse out of place.
  const rowLayout = props.secondaryContent ? Styles.twoRow : Styles.oneRow;

  const ClickableListRow = () => (
    <Clickable
      onClick={() => props.onClick?.(props.dataID)}
      className={cn(Styles.container, Styles.listRow, rowLayout)}
    >
      <div className={cn(Styles.avatar)}>{icon}</div>
      <Content />
    </Clickable>
  );

  // Plain, non-interactive row (no navigation, no click handler) — e.g. the staff list. Renders a
  // div so it doesn't announce as a focusable button the way Clickable would.
  const StaticListRow = () => (
    <div className={cn(Styles.container, Styles.listRow, rowLayout)}>
      <div className={cn(Styles.avatar)}>{icon}</div>
      <Content />
    </div>
  );

  if (props.href) return <LinkListRow />;
  if (props.onClick) return <ClickableListRow />;
  return <StaticListRow />;
};
